import {
    call,
    put,
    select,
    take,
    takeEvery,
    takeLatest,
    delay,
} from 'redux-saga/effects';

import * as actions from './actions';
import { MeStateType, StoreBasicDataApiStructure } from 'common/types';
import { repositories } from 'api';
import { fetchMe, updateMeCarts } from '../me/actions';
import { getChartToken } from 'common/selectors';
import {
    ADD_PROMOTIONAL_SET,
    ADD_TO_SHOP_CART,
    UPDATE_CART_SHOP,
    CART_ITEM_DELETE_REQUEST,
    CART_ITEM_QUANTITY_REQUEST,
    CART_ITEMS_POST_REQUEST,
    CART_PROMOTIONAL_SET_REQUEST,
    CARTS_POST_REQUEST,
    CHANGE_PRODUCT_QUANTITY,
    FETCH_CART_REQUEST,
    REMOVE_ITEM_FROM_SHOP_CART,
    CART_PROMOTIONAL_SET_SUCCEEDED,
} from './types';
import { transformCartItems } from './selectors';

export {
    actions,
};

const cartItem = new repositories.cart.CartItem();

function* removeCartItem({ payload, }: any) {
    try {
        const token = yield select(getChartToken);

        if (!token) {
            return;
        }

        const { data, } = yield call([ cartItem, 'remove' ], token, payload);

        yield put(actions.cartItemDelete.success(data));
    } catch (error) {}
}

const cart = new repositories.cart.Cart();

function* fetchCart() {
    try {
        yield take(fetchMe.success);
        const token = yield select(getChartToken);

        if (!token) {
            return;
        }

        const { data, } = yield call([ cart, 'get', ], token);

        yield put(actions.fetchCart.success(data));
        yield put({ type: UPDATE_CART_SHOP, data: transformCartItems(data.items), });
    } catch (error) {}
}

const carts = new repositories.carts.Carts();

function* postCarts() {
    try {
        const { data, } = yield call([ carts, 'save', ]);

        yield put(actions.cartsPost.success(data));
    } catch (error) {}
}

const cartItems = new repositories.cart.CartItems();

function* postCartItems({ payload, }: any) {
    try {
        const token = yield select(getChartToken);
        const { data, } = yield call([ cartItems, 'save' ], token, payload);

        yield delay(500);
        yield put(actions.cartItemsPost.success(data));
        yield call(payload.callback);
    } catch (error) {
        const { response: { data, }, } = error;

        const payloadError = {
            code: data.code,
            message: data.message,
            data: data.errors,
        };

        const messages = Object
            .values<Array<string>>(data.errors)
            .map(message => message.join('\t'))
            .join('\t');

        yield put(actions.cartItemsPost.failure(payloadError));
        yield call(payload.callback, { error: true, messages, });
    }
}

type InfoType = {
    info: StoreBasicDataApiStructure<repositories.InfoType>,
};

const getAvailableCharts = ({ me, }: MeStateType) => me.data.carts;
const getInfoCodeChannel = ({ info, }: InfoType) => info.data.code;

function* createCartShop() {
    const carts = yield select(getAvailableCharts);
    const channelCode = yield select(getInfoCodeChannel);

    const foundCart = carts.find((cart: any) => cart.channel === channelCode);

    if (!foundCart) {
        yield put(actions.cartsPost.request());
        const data = yield take(actions.cartsPost.success);
        const { channel, tokenValue, } = data.payload;

        yield put(updateMeCarts({ channel, token: tokenValue, }));
    }
}

function* shopCartAddingHandler({ payload, meta, }: any) {
    yield createCartShop();

    yield put(actions.cartItemsPost.request({ productCode: payload.code, quantity: payload.quantity, callback: meta.callback, }));
    const data = yield take(actions.cartItemsPost.success);

    yield put({
        type: UPDATE_CART_SHOP,
        data: transformCartItems(data.payload.items),
    });
}

function* shopCartRemovingHandler({ payload }: any) {
    yield put(actions.cartItemDelete.request(payload.id));

    const data = yield take(actions.cartItemDelete.success);

    yield put({
        type: UPDATE_CART_SHOP,
        data: transformCartItems(data.payload.items),
    });
}

function* changeItemQuantity({ payload }: any) {
    try {
        const { id, token, quantity } = payload;
        const { data } = yield call([ cart, 'put'], token, id, quantity);

        yield put(actions.cartItemQuantity.success(data));
        yield put({ type: UPDATE_CART_SHOP, data: transformCartItems(data.items), });

    } catch (error) {}
}

function* changeProductQuantityHandler({ payload: { id, quantity } }: any) {
    const token = yield select(getChartToken);

    yield put(actions.cartItemQuantity.request({ token, id, quantity }));
}

function* cartPromotionalSet({ payload }: any) {
    try {
        const { token, payloadData } = payload;
        const { data } = yield call([ cart, 'post'], token, payloadData.data);

        yield call(payload.callback);

        yield put({
            type: UPDATE_CART_SHOP,
            data: transformCartItems(data.items),
        });

        yield put({ type: CART_PROMOTIONAL_SET_SUCCEEDED, });
    } catch (error) {}
}

function* cartPromotionalSetHandler({ payload, meta }: any) {
    yield createCartShop();
    const token = yield select(getChartToken);

    yield put(actions.cartPromotionalSet.request({ token, payloadData: payload, callback: meta.callback }, ));
}

function* shopCartSaga() {
    yield takeLatest(FETCH_CART_REQUEST, fetchCart);
    // Added "takeLatest" instead "takeEvery" to prevent adding items when quantity exceeded, it's hack and will be better base on code error not on took latest action.
    yield takeLatest(ADD_TO_SHOP_CART, shopCartAddingHandler);
    yield takeEvery(CART_ITEM_DELETE_REQUEST, removeCartItem);
    yield takeEvery(REMOVE_ITEM_FROM_SHOP_CART, shopCartRemovingHandler);
    yield takeEvery(CARTS_POST_REQUEST, postCarts);
    yield takeEvery(CART_ITEMS_POST_REQUEST, postCartItems);
    yield takeEvery(CHANGE_PRODUCT_QUANTITY, changeProductQuantityHandler);
    yield takeEvery(CART_ITEM_QUANTITY_REQUEST, changeItemQuantity);
    yield takeEvery(ADD_PROMOTIONAL_SET, cartPromotionalSetHandler);
    yield takeEvery(CART_PROMOTIONAL_SET_REQUEST, cartPromotionalSet);
}

export default shopCartSaga;
